import { throttle } from 'lodash';
import UserIntegrations from '../collections/UserIntegrations';
import UserIntegration from '../models/UserIntegration';
import ContactsUnlinked from '../collections/ContactsUnlinked';
import IntegrationService from '../../services/integration';
import CrmUsers from 'store/collections/CrmUsers';
import CrmUser from 'store/models/CrmUser';
import { baseApi } from 'core/api';
import { useToastStore, useUserStore } from 'store/modules';

const state = () => ({
    _all: null,
    _loading: false,
    _userIntegrations: null,
    _userIntegrationsTeams: null,
    _fields: [],
    _integrationsSettingsPermit: false,
    _hubSpotContactLists: [],
    _loadedHubSpotContactLists: [],
    _integrationMembers: [],
    _integrationMembersLoading: false,
    _integrationMembersPagination: {
        current_page: 1,
        per_page: 10,
    },
    _organizationSettings: null,
    _organizationSettingsLoading: false,
    _customChannels: [],
    _customChannelsLoading: false,
    _customChannelsPagination: {
        current_page: 1,
        per_page: 10,
    },
});

const getters = {
    integrations: (state) => state._all,
    userIntegrations: (state) => state._userIntegrations,
    userIntegrationsTeams: (state) => state._userIntegrationsTeams,
    integrationFields: (state) => state._fields,
    integrationsSettingsPermit: (state) => state._integrationsSettingsPermit,
    integrationsLoading: (state) => state._loading,
    hubSpotContactLists: (state) => state._hubSpotContactLists,
    loadedHubSpotContactLists: (state) => state._loadedHubSpotContactLists,
    integrationMembers: (state) => state._integrationMembers,
    integrationMembersLoading: (state) => state._integrationMembersLoading,
    integrationMembersPagination: (state) => state._integrationMembersPagination,
    integrationOrganizationSettings: (state) => state._organizationSettings,
    integrationOrganizationSettingsLoading: (state) => state._organizationSettingsLoading,
    salesforceSettingsPackage: (state) =>
        Array.isArray(state._organizationSettings)
            ? state._organizationSettings.find(
                  (setting) => setting.key === 'salesforce_package_type'
              )
            : null,
    customChannels: (state) => state._customChannels,
    customChannelsLoading: (state) => state._customChannelsLoading,
    customChannelsPagination: (state) => state._customChannelsPagination,
};

const utils = {
    getIntegrations: (_this) => {
        if (_this._loading) return;

        _this._setIntegrationsLoading();

        return baseApi
            .get('core/integrations')
            .then((response) => {
                _this._setIntegrationsLoaded(response.data);
                return response;
            })
            .catch((error) => {
                _this._setIntegrationsLoadError(error);
                return error;
            });
    },
};

// throttle actions
const throttleGetIntegrations = throttle(utils.getIntegrations, 3000, { trailing: false });

const actions = {
    async getIntegrationOrganizationSettings() {
        if (this._organizationSettingsLoading) return;

        this._setIntegrationsOrganizationSettingsLoading();

        return baseApi
            .get('integration/organization-settings')
            .then((response) => {
                this._setIntegrationsOrganizationSettingsLoaded(response.data.data);

                return response.data.data;
            })
            .catch((error) => {
                const message = error.response?.data?.message || 'Failed to load settings';

                this._setIntegrationsOrganizationSettingsError(message);

                useToastStore().showToast({
                    type: 'danger',
                    message,
                });

                return error;
            });
    },

    updateIntegrationOrganizationSettings(params) {
        this._setIntegrationsOrganizationSettingsLoading();

        return baseApi
            .put('integration/organization-settings', params)
            .then((response) => {
                this._setIntegrationsOrganizationSettingsLoaded(response.data.data);

                useToastStore().showToast({
                    type: 'success',
                    message: 'Settings saved.',
                });

                return response;
            })
            .catch((error) => {
                const message = error.response?.data?.message || 'Failed to save settings';

                this._setIntegrationsOrganizationSettingsError(message);

                useToastStore().showToast({
                    type: 'danger',
                    message,
                });

                return error;
            });
    },

    getIntegrations({ throttle = true } = {}) {
        if (throttle) {
            return throttleGetIntegrations(this);
        }

        return utils.getIntegrations(this);
    },

    getIntegrationsByIdFields({ id, params = {}, update = true }) {
        return baseApi.get(`core/integrations/${id}/fields`, { params }).then((response) => {
            if (update) {
                this._setFieldsLoadedForIntegration({
                    key: id,
                    fields: response.data,
                });
            }

            return response.data;
        });
    },

    getIntegrationsByIdMergeFields({ id, params = {} }) {
        return baseApi.get(`core/integrations/${id}/merge-fields`, { params }).then((response) => {
            return response.data;
        });
    },

    updateIntegrationsByIdConnect({ integration, form }) {
        return form.post(`core/integrations/${integration}/connect`).then((response) => {
            this._setUserIntegrationAdded(response.data);
            return response;
        });
    },

    getIntegrationsByIdCheck(integration) {
        return baseApi.get(`core/integrations/${integration}/check`).then((response) => {
            this._setUserIntegrationAdded(response.data);
            return response;
        });
    },

    getIntegrationUsers({ integration, search = '' }) {
        this._setUserIntegrationMembersLoading(true);

        let url = `/core/integrations/${integration}/users`;

        if (search?.trim()) {
            url += `?query=${encodeURIComponent(search.trim())}`;
        }

        return baseApi
            .get(url, {
                params: {
                    page: this._integrationMembersPagination.current_page,
                    limit: this._integrationMembersPagination.per_page,
                },
            })
            .then((response) => {
                this._setUserIntegrationMembersLoad(response.data);
                return response;
            })
            .finally(() => {
                this._setUserIntegrationMembersLoading(false);
            });
    },

    updateIntegrationsByIdDisconnect({ integration, form }) {
        if (IntegrationService.useMicroIntegration(integration)) {
            return baseApi.put(`integration/disconnect/${integration}`).then((response) => {
                this._setUserIntegrationRemoved(form);
                return response;
            });
        }
        return form.put(`core/integrations/${integration}/disconnect`).then((response) => {
            this._setUserIntegrationRemoved(form);
            return response;
        });
    },

    updateIntegrationsByIdUsersById({ integration, crmUserId, userId }) {
        let data = {
            attribute: 'user_id',
            value: userId,
        };

        return baseApi
            .patch(`core/integrations/${integration}/users/${crmUserId}`, data)
            .then((response) => {
                this._setCrmUserUpdated(response.data);
                return response;
            });
    },

    integrationConnected(userIntegration) {
        this._setUserIntegrationAdded(userIntegration);
    },

    integrationDisconnected(payload) {
        this._setUserIntegrationRemoved(payload.userIntegration);
        if (payload.notifyUser) {
            useUserStore()._setUserIntegrationDisconnectedSet();
        }
    },

    updateIntegrationsRefresh(key) {
        this._setUserIntegrationMembersLoading(true);

        return baseApi
            .post('core/integrations/refresh', {
                integration: key,
            })
            .then((response) => {
                return response;
            });
    },

    autoMapUsers(key) {
        this._setUserIntegrationMembersLoading(true);

        return baseApi.post(`integrations/${key}/auto-mapping-users`).then((response) => {
            return response;
        });
    },

    getHubspotListsContact() {
        return baseApi
            .get('core/hubspot/lists/contact')
            .then((response) => {
                this._setHubspotContactListsLoading(response.data);
                return response.data;
            })
            .finally(() => {
                this._setUserIntegrationMembersLoading(false);
            });
    },

    updateHubspotListsProcess(form) {
        return form.post('core/hubspot/lists/process').then(({ data }) => {
            return data;
        });
    },

    getHubspotListsContactProcessed() {
        return baseApi.get('core/hubspot/lists/contact/processed').then((response) => {
            this._setLoadedHubspotContactLists(response.data);
            return response.data;
        });
    },

    getIntegrationsInfusionsoftTags({ params }) {
        return baseApi.get('core/integrations/infusionsoft/tags', { params }).then((response) => {
            return response.data;
        });
    },

    updateIntegrationsInfusionsoftTagsApply({ form }) {
        return form.put('core/integrations/infusionsoft/tags/apply').then((response) => {
            return response.data;
        });
    },

    createIntegrationsInfusionsoftTasksCreate({ form }) {
        return form.put('core/integrations/infusionsoft/tasks/create').then((response) => {
            return response.data;
        });
    },

    createIntegrationsInfusionsoftNotesCreate({ form }) {
        return form.put('core/integrations/infusionsoft/notes/create').then((response) => {
            return response.data;
        });
    },

    updateIntegrationsActivecampaignTagsApply({ form }) {
        return form.put('core/integrations/activecampaign/tags/apply').then((response) => {
            return response.data;
        });
    },

    createIntegrationsActivecampaignNotesCreate({ form }) {
        return form.put('core/integrations/activecampaign/notes/create').then((response) => {
            return response.data;
        });
    },

    updateIntegrationsContactsByIdUnlink({ form, id }) {
        return form.put(`core/integrations/contacts/${id}/unlink`).then((response) => {
            return response.data;
        });
    },

    getHubspotSyncConflicts() {
        return baseApi.get('core/hubspot/sync/conflicts').then((response) => {
            return new ContactsUnlinked(response.data).models;
        });
    },

    updateHubspotSyncConflictsResolve(data) {
        return baseApi.post('core/hubspot/sync/conflicts/resolve', data).then((response) => {
            return response.data;
        });
    },

    getIntegrationsSalesforceVersionsActive() {
        return baseApi.get('core/integrations/salesforce/versions/active').then((response) => {
            return response.data;
        });
    },

    setIntegrationNumberVendors({ form, key, accountSid, authToken }) {
        return form
            .post(`core/integrations/number-vendors/${key}/connect`, {
                account_sid: accountSid,
                auth_token: authToken,
            })
            .catch((e) => {
                useToastStore().showToast({
                    type: 'danger',
                    message: e.response.data.error,
                });

                return e.response;
            });
    },

    integrationMembersPaginationUpdate({ page, length, integration, search, reload = true }) {
        this._setUserIntegrationMembersPaginationUpdate({ page, length });

        if (reload) {
            this.getIntegrationUsers({ integration, search });
        }
    },

    userIntegrationLoaded() {
        this._setUserIntegrationMembersLoading(false);
    },

    getHubspotChannels() {
        this._setHubspotCustomChannelsLoading(true);
        return baseApi
            .get('core/integrations/hubspot/channels', {
                params: {
                    page: this._customChannelsPagination.current_page,
                    limit: this._customChannelsPagination.per_page,
                },
            })
            .then((response) => {
                this._setHubspotCustomChannelsLoad(response.data);
                return response;
            })
            .finally(() => {
                this._setHubspotCustomChannelsLoading(false);
            });
    },

    refreshHubspotChannels() {
        this._setHubspotCustomChannelsLoading(true);
        return baseApi
            .post(
                `core/integrations/hubspot/channels?page=${this._customChannelsPagination.current_page}&limit=${this._customChannelsPagination.per_page}`
            )
            .then((response) => {
                this._setHubspotCustomChannelsLoad(response.data);
                return response;
            })
            .finally(() => {
                this._setHubspotCustomChannelsLoading(false);
            });
    },

    hubspotChannelsUpdateLength({ length }) {
        this._setHubspotCustomChannelsPaginationUpdate({ length });
        this.getHubspotChannels();
    },

    hubspotChannelsUpdatePage({ page }) {
        this._setHubspotCustomChannelsPaginationUpdate({ page });
        this.getHubspotChannels();
    },

    _setIntegrationsOrganizationSettingsLoading() {
        this._organizationSettingsLoading = true;
    },

    _setIntegrationsOrganizationSettingsLoaded(data) {
        this._organizationSettingsLoading = false;
        this._organizationSettings = data;
    },

    _setIntegrationsOrganizationSettingsError() {
        this._organizationSettingsLoading = false;
        this._organizationSettings = null;
    },

    _setHubspotContactListsLoading(data) {
        this._hubSpotContactLists = data;
    },

    _setLoadedHubspotContactLists(data) {
        this._loadedHubSpotContactLists = data;
    },

    _setIntegrationsLoading() {
        this._loading = true;
    },

    _setIntegrationsLoaded(data) {
        const sort = {
            hubspot: 0,
            salesforce: 1,
            activecampaign: 2,
            infusionsoft: 3,
            marketo: 4,
            pipedrive: 5,
            slack: 6,
            aircall: 7,
            google: 8,
            zipwhip: 9,
            intercom: 10,
            zapier: 11,
            make: 12,
            webhook: 13,
        };

        const integrationsAll = data.all
            .sort((a, b) => sort[a.key] - sort[b.key])
            .map((item) => {
                if (item.key === 'make') {
                    item.name = 'Make (formerly Integromat)';
                }

                return item;
            });

        this._all = integrationsAll;
        this._userIntegrations = new UserIntegrations(data.userIntegrations).models;
        this._userIntegrationsTeams = data.integrationTeams || [];
        this._integrationsSettingsPermit = data.settingsPermit;
        this._loading = false;
    },

    _setIntegrationsLoadError() {
        this._all = false;
        this._loading = false;
    },

    _setFieldsLoadedForIntegration({ key, fields }) {
        this._fields.push({
            key: key,
            fields: fields,
        });
    },

    _setUserIntegrationAdded(userIntegration) {
        if (!Array.isArray(this._userIntegrations)) {
            this._userIntegrations = new UserIntegrations([userIntegration]).models;
        } else {
            if (this._userIntegrations.findIndex((o) => o.id === userIntegration.id) === -1) {
                this._userIntegrations.push(new UserIntegration(userIntegration));
            }
        }
    },

    _setUserIntegrationRemoved(form) {
        if (!this._userIntegrations) {
            return;
        }

        this._userIntegrations = this._userIntegrations.filter((o) => o.id !== form.id);
    },

    _setCrmUserUpdated(updatedCrmUser) {
        let newUserIntegrations = this._userIntegrations.map((userIntegration) => {
            userIntegration.crm_users = userIntegration.crm_users.map((crmUser) => {
                return crmUser.id === updatedCrmUser.id ? updatedCrmUser : crmUser;
            });

            return userIntegration;
        });

        this._integrationMembers = this._integrationMembers.map((crmUser) => {
            return crmUser.id === updatedCrmUser.id ? new CrmUser(updatedCrmUser) : crmUser;
        });

        this._userIntegrations = new UserIntegrations(newUserIntegrations).models;
    },

    _setUserIntegrationMembersPaginationUpdate({ page, length }) {
        if (length) {
            this._integrationMembersPagination.current_page = 1;
            this._integrationMembersPagination.per_page = length;
        } else {
            this._integrationMembersPagination.current_page = page;
        }
    },

    _setUserIntegrationMembersLoading(status) {
        this._integrationMembersLoading = status;
    },

    _setUserIntegrationMembersLoad(data) {
        const members = new CrmUsers(data.data);
        this._integrationMembers = members.models;

        this._integrationMembersPagination = {
            current_page: data.current_page,
            from: data.from,
            last_page: data.last_page,
            per_page: data.per_page,
            to: data.to,
            total: data.total,
        };
    },

    _setHubspotCustomChannelsLoading(status) {
        this._customChannelsLoading = status;
    },

    _setHubspotCustomChannelsLoad(data) {
        this._customChannels = data.data;

        this._customChannelsPagination = {
            current_page: data.current_page,
            from: data.from,
            last_page: data.last_page,
            per_page: data.per_page,
            to: data.to,
            total: data.total,
        };
    },

    _setHubspotCustomChannelsPaginationUpdate({ page, length }) {
        if (length) {
            this._customChannelsPagination.current_page = 1;
            this._customChannelsPagination.per_page = length;
        } else {
            this._customChannelsPagination.current_page = page;
        }
    },
};

export default {
    state,
    getters,
    actions,
};
